import type { NetworkDoc, NetworkNode } from '../../../types'

// [자동 정렬] — Edge 서버를 맨 위에 두고 연결을 따라 아래로 펼친다 (나무 모양).
// 하위 망(스위치·변환기 등 아래에 또 물린 장비)은 서브트리로, 끝 장비들은 부모 바로 아래 묶음으로.
// 끝 장비가 많으면 두 줄 벽돌 모양으로 엇갈려 놓는다 — 꺾은 선(smoothstep)의 가로 줄기가 모두 장비 줄 위 틈을 지나 노드를 가로지르지 않는다.

const COL = 250        // 가로 한 칸 (노드 폭 180~260 + 틈)
const LEVEL = 220      // 층 간격 — 벽돌 아랫줄로 가는 선의 가로 줄기도 윗줄보다 위에 오게
const BRICK = 100      // 벽돌 아랫줄 내림
const BRICK_FROM = 5   // 끝 장비가 이만큼 이상이면 벽돌로
const LOOSE_COLS = 6   // 아무 데도 안 이어진 노드 줄 수
const PRIORITY: Record<string, number> = { edge: 0, router: 1, switch: 2, hub: 3, converter: 4, equipment: 5, pc: 6, other: 7 }

type Tree = { node: NetworkNode; kids: Tree[] }

export function autoLayout(doc: NetworkDoc): NetworkDoc {
  if (!doc.nodes.length) return doc
  const byKey = new Map(doc.nodes.map(n => [n.key, n]))
  const adj = new Map<string, Set<string>>(doc.nodes.map(n => [n.key, new Set<string>()]))
  for (const l of doc.links) {
    if (l.from === l.to || !byKey.has(l.from) || !byKey.has(l.to)) continue
    adj.get(l.from)!.add(l.to)
    adj.get(l.to)!.add(l.from)
  }

  // 지금 왼쪽→오른쪽 순서를 살린다
  const byX = (a: NetworkNode, b: NetworkNode) => a.x - b.x || a.y - b.y || a.key.localeCompare(b.key)
  const visited = new Set<string>()
  const parentOf = new Map<string, string>()
  const build = (root: NetworkNode): Tree => {
    const tree: Tree = { node: root, kids: [] }
    visited.add(root.key)
    const queue = [tree]
    while (queue.length) {
      const t = queue.shift()!
      const next = [...adj.get(t.node.key)!].map(k => byKey.get(k)!).filter(n => !visited.has(n.key)).sort(byX)
      for (const n of next) {
        visited.add(n.key)
        parentOf.set(n.key, t.node.key)
        const kid: Tree = { node: n, kids: [] }
        t.kids.push(kid)
        queue.push(kid)
      }
    }
    return tree
  }

  // 덩어리마다 뿌리 하나 — Edge 서버 덩어리 먼저, 나머지는 연결 많은 장비가 뿌리
  const roots: Tree[] = []
  const edge = doc.nodes.find(n => n.kind === 'edge')
  if (edge) roots.push(build(edge))
  const rest = () => doc.nodes.filter(n => !visited.has(n.key) && adj.get(n.key)!.size > 0)
  for (let r = rest(); r.length; r = rest()) {
    const pick = [...r].sort((a, b) => adj.get(b.key)!.size - adj.get(a.key)!.size || (PRIORITY[a.kind] ?? 9) - (PRIORITY[b.kind] ?? 9) || byX(a, b))[0]
    roots.push(build(pick))
  }
  const loose = doc.nodes.filter(n => !visited.has(n.key)).sort(byX)

  const leaves = (t: Tree) => t.kids.filter(k => !k.kids.length)
  const subs = (t: Tree) => t.kids.filter(k => k.kids.length)
  const leafWidth = (ls: Tree[]) => {
    const n = ls.length
    return n === 0 ? 0 : n < BRICK_FROM ? n * COL : (n - 1) * (COL / 2) + COL
  }
  const widths = new Map<Tree, number>()
  const width = (t: Tree): number => {
    let w = widths.get(t)
    if (w == null) {
      w = Math.max(COL, leafWidth(leaves(t)) + subs(t).reduce((s, k) => s + width(k), 0))
      widths.set(t, w)
    }
    return w
  }

  const pos = new Map<string, { x: number; y: number }>()
  const place = (t: Tree, left: number, y: number) => {
    const w = width(t)
    pos.set(t.node.key, { x: left + (w - COL) / 2, y })
    const ls = leaves(t), ss = subs(t)
    const inner = leafWidth(ls) + ss.reduce((s, k) => s + width(k), 0)
    let x = left + (w - inner) / 2
    // 하위 망을 양옆으로 나누고 끝 장비 묶음을 부모 바로 아래 가운데에
    const half = Math.ceil(ss.length / 2)
    for (const s of ss.slice(0, half)) { place(s, x, y + LEVEL); x += width(s) }
    const brick = ls.length >= BRICK_FROM
    ls.forEach((l, i) => pos.set(l.node.key, brick
      ? { x: x + i * (COL / 2), y: y + LEVEL + (i % 2) * BRICK }
      : { x: x + i * COL, y: y + LEVEL }))
    x += leafWidth(ls)
    for (const s of ss.slice(half)) { place(s, x, y + LEVEL); x += width(s) }
  }

  let left = 0
  for (const r of roots) { place(r, left, 0); left += width(r) + COL / 2 }
  const bottom = pos.size ? Math.max(...[...pos.values()].map(p => p.y)) + LEVEL : 0
  loose.forEach((n, i) => pos.set(n.key, { x: (i % LOOSE_COLS) * COL, y: bottom + Math.floor(i / LOOSE_COLS) * 120 }))

  // 첫 뿌리(대개 Edge 서버)는 제자리에 — 보던 화면에서 크게 튀지 않게
  const anchor = roots[0]?.node ?? loose[0]
  const a = pos.get(anchor.key)!
  const dx = anchor.x - a.x, dy = anchor.y - a.y
  const snap = (v: number) => Math.round(v / 10) * 10

  return {
    ...doc,
    nodes: doc.nodes.map(n => {
      const p = pos.get(n.key)!
      const x = snap(p.x + dx), y = snap(p.y + dy)
      return x === n.x && y === n.y ? n : { ...n, x, y }
    }),
    // 나무 줄기 연결은 부모 아래 점 → 자식 위 점으로
    links: doc.links.map(l => {
      const side = parentOf.get(l.to) === l.from ? { fromSide: 'b' as const, toSide: 't' as const }
        : parentOf.get(l.from) === l.to ? { fromSide: 't' as const, toSide: 'b' as const }
          : null
      return side && (l.fromSide !== side.fromSide || l.toSide !== side.toSide) ? { ...l, ...side } : l
    }),
  }
}
